import { Headphones, Send, Undo2, UserPlus, Users } from "lucide-react";
import * as React from "react";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  useColleagues, useEndRoomConsult, useInviteToRoom, useLeadCard, useRoomCall, useRoomHold,
} from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { leadName } from "@/lib/campaign";
import {
  alreadyOnCall, colleagueLabel, consultState, directRoomCallId, holdPath, inviteText,
  listedRoomCallId, rosterLabels, rosterShown, shouldTakeCaller, type ConsultState,
} from "@/lib/rooms";
import type { Colleague, ConsultOutcome, InviteMode, RoomCall } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { BriefingEndings, useAcdCall, useRoomHold as useAcdCallerHold } from "@/softphone/acd-call";
import type { ConferenceState } from "@/softphone/conference";
import { ConferenceRoster } from "@/softphone/conference-roster";
import { useSoftphone, type Call } from "@/softphone/softphone-context";

/**
 * A call on a rep's own number, in a room of its own (ADR-032): what the phone shows and asks
 * for on such a call, over and above what any call gets.
 *
 * Nothing here is gated on the ACD. The gate is the room the phone's leg joined: the switch
 * pushes the roster of a direct room to the phone like any other, and its name says what kind of
 * room it is (`directRoomCallId`). From there the phone reads the server's picture of the room
 * (`GET /phone/room/calls/{call_id}`) for the things the roster does not carry — who brought a
 * colleague in and why, whose phone is ringing for the call — and the roster itself for the
 * things it does, which arrive faster: who is on the call, and whether the caller is on hold.
 */

/**
 * Whether the roster shows an outside party on hold.
 *
 * Only ever a yes: mod_conference reports `audio.onHold` from the channel's own hold flag, which
 * a hold done in the room (`conference <room> hold`) does not set, so a held caller can read as
 * not held here. The server's picture of the room (`caller_held`, from the switch's own hold
 * events) is what decides; this adds nothing the phone would otherwise miss, and takes nothing
 * away.
 */
export function callerHeldOnRoster(conference: ConferenceState | null): boolean {
  if (!conference) return false;
  return conference.participants.some((p) => (p.kind === "caller" || p.kind === "lead") && p.onHold);
}

/**
 * Whether the outside party on this call is on hold in the room the call lives in — a call on the
 * rep's own number (ADR-032) or one the ACD connected (ADR-041). The server's picture of the room,
 * else the roster's own flag.
 *
 * Never the rep's ACD record on its own. The record says who the REP held (`callerHeld`), and the
 * ACD holds the client itself without touching it: on music while a setter briefs a closer
 * (ADR-027), and again while a handoff waits. A phone that read only the record would show such a
 * call as connected while the caller listened to music.
 */
export function useCallerHeld(callId: string | null): boolean {
  const phone = useSoftphone();
  const listed = callId !== null && listedRoomCallId(phone.conference) === callId;
  const { data: room } = useRoomCall(callId, { enabled: listed });
  if (!listed) return false;
  return Boolean(room?.caller_held) || callerHeldOnRoster(phone.conference);
}

/**
 * How many people are on a call in a room — a call on the rep's own number (ADR-032) or one the
 * ACD connected (ADR-041) — as the server counts them, which never counts a supervisor listening
 * in. Null for any other call, and until the server has answered: `describeParty` then counts
 * the switch's roster.
 */
export function useRoomPeople(callId: string | null): number | null {
  const phone = useSoftphone();
  const listed = callId !== null && listedRoomCallId(phone.conference) === callId;
  const { data: room } = useRoomCall(callId, { enabled: listed });
  return listed && room?.members.length ? room.members.length : null;
}

/**
 * The server's picture of the room one of this phone's legs is in.
 *
 * Read for the phone's leg in a direct room, and for an inbound call still ringing, which may be
 * a colleague's phone ringing for somebody else's call. A leg on no room call answers with no
 * room, and nothing else changes for that call.
 */
export function useDirectRoom(
  callId: string | null,
  { ringing = false }: { ringing?: boolean } = {},
): { isDirect: boolean; room: RoomCall | null } {
  const phone = useSoftphone();
  const isDirect = callId !== null && directRoomCallId(phone.conference) === callId;
  const { data } = useRoomCall(callId, { enabled: isDirect || ringing });
  return { isDirect, room: data ?? null };
}

const HOLD_SETTLES_MS = 4_000;

/**
 * Hold on a direct call: the caller is held in the room, on music, and the rep's own leg stays
 * up. The phone's own hold would do the opposite — hold the rep and leave the caller hearing
 * nobody — so the tile goes through the server here. Pending until the roster's own flag
 * agrees, or four seconds, whichever is first.
 */
export function useDirectHold(callId: string | null): {
  held: boolean;
  pending: boolean;
  toggle: () => void;
} {
  const toast = useToast();
  const hold = useRoomHold();
  const [asked, setAsked] = React.useState<boolean | null>(null);
  const held = useCallerHeld(callId);

  React.useEffect(() => {
    if (asked === null) return;
    if (asked === held) {
      setAsked(null);
      return;
    }
    const timer = window.setTimeout(() => setAsked(null), HOLD_SETTLES_MS);
    return () => window.clearTimeout(timer);
  }, [asked, held]);

  const toggle = () => {
    if (callId === null || asked !== null) return;
    const next = !held;
    setAsked(next);
    hold.mutate(
      { callId, held: next },
      {
        onError: (e) => {
          setAsked(null);
          toast.error(
            next ? "Could not hold the caller" : "Could not take the caller off hold",
            e instanceof ApiError ? e.message : "Please try again.",
          );
        },
      },
    );
  };
  return { held, pending: asked !== null, toggle };
}

/**
 * Hold, for whoever is on the call who is not this rep — the one rule, for every surface that
 * offers it.
 *
 * Three commands underneath, and which one is right is a fact about the call, not about the
 * control:
 *
 *   ACD room (ADR-041)   `POST /acd/calls/{caller}/hold`. The ACD owns the caller there — it
 *                        holds them for a handoff — so core-api REFUSES `/phone/room/hold` on
 *                        one of its rooms (409, `room_calls.room_of`): a second path to the same
 *                        switch commands would act behind the ACD's back.
 *   direct room (ADR-032) `POST /phone/room/hold`, which holds every member who is not a rep.
 *   plain bridged call   the phone's own hold, over verto.
 *
 * On a room call the phone's own hold is always wrong: it holds the REP's leg and leaves the
 * caller hearing nobody.
 *
 * Both room paths go through the server rather than over verto because **mod_conference does not
 * route `hold` to the browser**: `conference_event_mod_channel_handler` takes kick, mute/unmute/
 * tmute, deaf/undeaf, vmute/unvmute/tvmute, the volume and video commands, play/stop, recording
 * and transfer — and nothing else. `hold`/`unhold` exist only in the conference API table
 * (`conference_api.c`), which the verto mod channel does not expose. So mute and deaf can go
 * browser → switch, and hold cannot.
 */
export function useCallerHold(call: Call | null): {
  held: boolean;
  pending: boolean;
  /** False on a call with no hold to offer — none of the three applies. */
  applies: boolean;
  toggle: () => void;
} {
  const phone = useSoftphone();
  const acd = useAcdCall();
  const path = holdPath(call?.id ?? null, acd, phone.conference);
  const acdHold = useAcdCallerHold(path === "acd" ? acd : null);
  const directHold = useDirectHold(path === "direct" ? (call?.id ?? null) : null);
  // The room's own word, which is the only one for a hold the ACD does itself (`useCallerHeld`).
  const roomHeld = useCallerHeld(call?.id ?? null);

  if (call === null || path === null) {
    return { held: false, pending: false, applies: false, toggle: () => {} };
  }
  if (path === "direct") return { ...directHold, applies: true };
  if (path === "acd") return { ...acdHold, held: acdHold.held || roomHeld, applies: true };
  return {
    held: call.state === "held",
    pending: false,
    applies: true,
    toggle: () => void phone.toggleHold(call.id),
  };
}

/**
 * The people on a call that lives in a room, with what a rep can do about each of them: a call on
 * a rep's own number (ADR-032), or one the ACD connected, where a setter and a closer on a
 * three-way see the customer and each other (ADR-041).
 *
 * The roster's own names are the switch's caller ids, which on a leg the switch dialled out —
 * the rep's own, a colleague's, a closer the ACD rang — is the stand-in "Outbound Call" or the
 * caller's number. The server knows those legs by extension and name, so they are labelled
 * from its picture of the room, and the phone's own leg by the signed-in name. The same picture
 * says who is held in the room, which the roster's own flag does not. A supervisor listening in
 * is never listed: `conference.ts` leaves their row out.
 *
 * On a campaign call only a rep lists anybody. The ACD joins every rep as a moderator; a
 * supervisor's own phone, which it does not, is listening in, not on the call.
 *
 * The lead a campaign call is with is named from their own record, by the caller's channel the
 * rep's record names (`useAcdCall`), since the room has only a number for that leg and not always
 * theirs (`rosterLabels` in `lib/rooms.ts` holds the rule). The card is the one the call's own
 * card reads (`LeadCardPanel`), so naming the row asks for nothing more.
 *
 * `layout` and `onClose` are the roster's own (`ConferenceRoster`): a list in the phone's panel,
 * a tray along the foot of the Phone page.
 */
export function CallRoster({
  callId, layout = "list", onClose,
}: {
  callId: string;
  layout?: "list" | "tray";
  onClose?: () => void;
}) {
  const phone = useSoftphone();
  const { user } = useAuth();
  const listed = listedRoomCallId(phone.conference) === callId;
  const { data: room } = useRoomCall(callId, { enabled: listed });
  const acd = useAcdCall();
  const mine = acd !== null && acd.callId === callId ? acd : null;
  const { data: card } = useLeadCard(
    mine?.leadId ?? null,
    mine?.sessionId ?? mine?.callerChannelId ?? null,
  );
  if (!rosterShown(phone.conference, callId)) return null;
  const members = room?.members ?? [];
  const labels = rosterLabels({
    members,
    selfName: user?.display_name ?? null,
    selfChannelId: phone.conference?.participants.find((p) => p.isSelf)?.channelId ?? null,
    lead:
      mine !== null && card && card.id === mine.leadId
        ? {
            channelId: mine.callerChannelId,
            name: leadName(card) ?? formatPhoneNumber(card.phone),
          }
        : null,
    format: formatPhoneNumber,
  });
  const held = new Set(members.filter((m) => m.held).map((m) => m.channel_id));
  return (
    <ConferenceRoster
      title="On this call"
      callId={callId}
      labels={labels}
      held={held}
      memberTransfer={false}
      layout={layout}
      onClose={onClose}
    />
  );
}

/**
 * A briefing in progress on a direct call (ADR-032).
 *
 * For the rep who asked: the colleague's phone ringing, then the two of them talking with the
 * caller on hold, and the three ways it ends. For the colleague: that they are being briefed
 * while the caller waits — and, if the rep who was briefing them leaves, this phone takes the
 * caller off hold on its own and says so, because a caller on music with nobody coming is the
 * one outcome nobody wants.
 */
export function DirectConsultBar({ callId }: { callId: string }) {
  const phone = useSoftphone();
  const toast = useToast();
  const { isDirect, room } = useDirectRoom(callId);
  const state = consultState(room, phone.credentials?.extension ?? null);
  const finish = useEndRoomConsult();
  const hold = useRoomHold();
  const [error, setError] = React.useState<string | null>(null);
  const took = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isDirect || !shouldTakeCaller(state) || !room || took.current === room.room) return;
    took.current = room.room;
    const by = state?.role === "invitee" ? state.byName : "Your colleague";
    hold.mutate(
      { callId, held: false },
      {
        onSuccess: () => toast.info(`${by} left the call`, "The caller is with you now."),
        onError: () =>
          toast.error("The caller is still on hold", "Press Hold to take them off it."),
      },
    );
  }, [isDirect, state, room, callId, hold, toast]);

  if (!isDirect || state === null) return null;

  const act = (outcome: ConsultOutcome, current: ConsultState) => {
    setError(null);
    finish.mutate(
      { callId, outcome, withChannelId: "channelId" in current ? current.channelId : null },
      {
        onSuccess: () => {
          const who = "withName" in current ? current.withName : "your colleague";
          if (outcome === "complete") toast.success("Connected", `${who} has the caller.`);
          else if (outcome === "join") {
            toast.success("Three-way call", `You, ${who} and the caller are all on the call.`);
          } else toast.success("Taken back", "The caller is with you again.");
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not do that just now."),
      },
    );
  };
  const busy = (outcome: ConsultOutcome) =>
    finish.isPending && finish.variables?.outcome === outcome;

  return (
    <div className="w-full rounded-lg border border-accent-8 bg-surface-sunken px-3 py-2 text-left text-xs">
      {state.role === "asker" && state.phase === "ringing" && (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Spinner className="size-3.5 shrink-0" />
            <span className="truncate">Ringing {state.withName}…</span>
          </p>
          <p className="mt-0.5 text-text-muted">
            Your caller notices nothing until they answer; then they go on hold while you talk.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-9 w-full"
            loading={busy("cancel")}
            onClick={() => act("cancel", state)}
          >
            <Undo2 />
            Stop ringing them
          </Button>
        </>
      )}
      {state.role === "asker" && state.phase === "talking" && (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Headphones className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">You are talking to {state.withName}</span>
          </p>
          <p className="mt-0.5 text-text-muted">Your caller is on hold and cannot hear you.</p>
          <BriefingEndings busy={busy} onEnd={(outcome) => act(outcome, state)} />
        </>
      )}
      {state.role === "invitee" && (
        <>
          <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
            <Headphones className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <span className="truncate">{state.byName} is briefing you</span>
          </p>
          <p className="mt-0.5 text-text-muted">
            The caller is on hold and cannot hear you. {state.byName} decides when they join.
          </p>
        </>
      )}
      {error && (
        <Callout tone="danger" className="mt-2">
          {error}
        </Callout>
      )}
    </div>
  );
}

/**
 * On a ringing phone: whose call this is, when it is a colleague bringing this rep into theirs.
 * The caller id on the ring is the outside party's, as it should be; this says who is behind it.
 */
export function InviteStrip({ callId }: { callId: string }) {
  const { room } = useDirectRoom(callId, { ringing: true });
  const text = inviteText(room);
  if (!text) return null;
  return (
    <div className="w-full rounded-lg border border-border bg-surface-sunken px-3 py-2 text-left text-xs">
      <p className="flex min-w-0 items-center gap-1.5 font-medium text-text">
        <Users className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span>{text.title}</span>
      </p>
      <p className="mt-0.5 text-text-muted">{text.detail}</p>
    </div>
  );
}

/**
 * Who to bring in, and how: the transfer view's section for a direct call.
 *
 * Every endpoint in the rep's own realm, online or not, since that is the set their phone can
 * reach; the offline ones are listed and cannot be picked, so a rep sees that Maria is away
 * rather than wondering why she is missing. Three things to do with the one picked:
 *
 *   - **Warm transfer** (`consult`): their phone rings; when they answer, the caller goes on hold
 *     and the two reps talk. The bar on the call screen carries it from there.
 *   - **Conference** (`add`): their phone rings and they join the call as it is.
 *   - **Blind transfer**: the caller is sent to the colleague's number and this call ends here.
 *     They answer into a room of their own, with all of this.
 */
export function BringColleagueSection({
  callId, initialPick = null, onDone,
}: {
  callId: string;
  /** The colleague picked when the section first draws, scrolled into view in the list. */
  initialPick?: string | null;
  onDone: () => void;
}) {
  const phone = useSoftphone();
  const toast = useToast();
  const { room } = useDirectRoom(callId);
  const { data, isPending, isError } = useColleagues();
  const invite = useInviteToRoom();
  const [search, setSearch] = React.useState("");
  const [picked, setPicked] = React.useState<string | null>(initialPick);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const id = React.useId();

  const rows = React.useMemo(() => matchColleagues(data ?? [], search), [data, search]);
  const chosen = rows.find((c) => c.extension === picked) ?? null;
  const onCall = chosen ? alreadyOnCall(room, chosen.extension) : false;
  const offline = chosen?.registered === false;
  const sending = phone.handingOver.includes(callId);

  // Bring the colleague picked from elsewhere into view once the list has them, moving the
  // list's own scroll only: scrollIntoView would move the page under the rep as well.
  const listed = rows.some((c) => c.extension === initialPick);
  React.useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !row) return;
    list.scrollTop = row.offsetTop - (list.clientHeight - row.offsetHeight) / 2;
  }, [listed]);

  const bring = (mode: InviteMode) => {
    if (!chosen) return;
    setError(null);
    invite.mutate(
      { callId, extension: chosen.extension, mode },
      {
        onSuccess: () => {
          const who = colleagueLabel(chosen.extension, chosen.name);
          toast.success(
            `Ringing ${who}`,
            mode === "consult"
              ? "When they answer, your caller goes on hold while you talk."
              : "When they answer, they join the call.",
          );
          onDone();
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : "Could not ring your colleague."),
      },
    );
  };

  const send = async () => {
    if (!chosen) return;
    setError(null);
    try {
      await phone.blindTransfer(callId, chosen.extension);
      toast.success(
        `Sent to ${colleagueLabel(chosen.extension, chosen.name)}`,
        "Their phone rings with the caller. Your call ends once they are connected.",
      );
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not send the caller on.");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Name or extension"
        autoComplete="off"
        className="h-9 text-sm"
        aria-label="Find a colleague"
      />
      <div
        ref={listRef}
        role="listbox"
        aria-label="Colleagues"
        className="relative max-h-44 overflow-y-auto rounded-md border border-border p-1"
      >
        {isPending ? (
          <Empty>Loading…</Empty>
        ) : isError && !data ? (
          <Empty>Could not load the directory.</Empty>
        ) : rows.length === 0 ? (
          <Empty>{search ? "Nobody matches." : "No colleagues to bring in."}</Empty>
        ) : (
          rows.map((c) => {
            const here = alreadyOnCall(room, c.extension);
            const selected = c.extension === picked;
            return (
              <button
                key={c.extension}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => setPicked(selected ? null : c.extension)}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left outline-none transition-colors",
                  selected ? "bg-accent-3" : "hover:bg-surface-hover focus-visible:bg-surface-hover",
                )}
              >
                <StatusDot state={c.registered} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-text">
                    {c.name ?? `Extension ${c.extension}`}
                  </span>
                  <span className="block text-xs text-text-muted">
                    {c.name && <span className="font-mono">{c.extension} · </span>}
                    {here ? "On this call" : c.registered === false ? "Offline" : c.registered ? "Online" : "Unknown"}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <Button
          type="button"
          className="h-10 flex-col gap-0.5 text-xs"
          disabled={!chosen || onCall || offline || sending}
          loading={invite.isPending && invite.variables?.mode === "consult"}
          onClick={() => bring("consult")}
        >
          <Headphones />
          Warm transfer
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 flex-col gap-0.5 text-xs"
          disabled={!chosen || onCall || offline || sending}
          loading={invite.isPending && invite.variables?.mode === "add"}
          onClick={() => bring("add")}
        >
          <UserPlus />
          Conference
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 flex-col gap-0.5 text-xs"
          disabled={!chosen || onCall || offline}
          loading={sending}
          onClick={() => void send()}
        >
          <Send />
          Blind transfer
        </Button>
      </div>
      {chosen && offline && (
        <p className="text-xs text-text-muted">
          {colleagueLabel(chosen.extension, chosen.name)} is not online, so nothing would ring.
        </p>
      )}
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-2 py-4 text-center text-xs text-text-muted">{children}</p>;
}

/** Substring, case-insensitive, over the name and the extension. */
export function matchColleagues(colleagues: readonly Colleague[], search: string): Colleague[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...colleagues];
  return colleagues.filter(
    (c) => c.extension.includes(needle) || (c.name ?? "").toLowerCase().includes(needle),
  );
}
